import { useCallback, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { intl } from '@trello/i18n';
import { Spotlight } from '@trello/nachos/onboarding-experimental';
import { useIsPlannerPanelOpen } from '@trello/split-screen';
import { token } from '@trello/theme';

import { PLANNER_DISCOVERY_SPOTLIGHT_TARGET } from 'app/src/components/PlannerDiscoverySpotlight';
import outlookAnnouncementSpotlightImage from './outlook-spotlight.gif';
import { useIsEligibleForOutlookAnnouncement } from './useIsEligibleForOutlookAnnouncement';

interface OutlookAnnouncementSpotlightProps {
  onAction?: () => void;
  onClickTarget: () => void;
  target: string;
}

export const OutlookAnnouncementSpotlight: FunctionComponent<
  OutlookAnnouncementSpotlightProps
> = ({ onAction, onClickTarget, target }) => {
  const { isEligible } = useIsEligibleForOutlookAnnouncement();

  const { dismissOneTimeMessage } = useOneTimeMessagesDismissed();

  const isPlannerOpen = useIsPlannerPanelOpen();

  const dismiss = useCallback(() => {
    dismissOneTimeMessage('outlook-announcement-spotlight');
    // TODO TJC-7077 Fire button clicked analytic event
  }, [dismissOneTimeMessage]);

  const open = useCallback(() => {
    dismiss();
    onAction?.();
    // TODO TJC-7077 Fire button clicked analytic event
  }, [dismiss, onAction]);

  const onClickSpotlightTarget = useCallback(() => {
    dismiss();
    onClickTarget();
    // TODO TJC-7077 Fire button clicked analytic event
  }, [dismiss, onClickTarget]);

  const isTargetPanelNav = target === PLANNER_DISCOVERY_SPOTLIGHT_TARGET;

  const openAction = {
    onClick: open,
    text: intl.formatMessage({
      id: 'templates.outlook_announcement_spotlight.open',
      defaultMessage: 'Open',
      description: 'Open Planner button for Outlook announcement Spotlight',
    }),
  };
  const dismissAction = {
    onClick: dismiss,
    text: intl.formatMessage({
      id: 'templates.outlook_announcement_spotlight.dismiss',
      defaultMessage: 'Dismiss',
      description: 'Dismiss button for Outlook announcement Spotlight',
    }),
  };

  if (!isEligible || (isTargetPanelNav && isPlannerOpen)) {
    return null;
  }

  return (
    <Spotlight
      heading={intl.formatMessage({
        id: 'templates.outlook_announcement_spotlight.we-now-support',
        defaultMessage: 'We now support Outlook Calendar',
        description: 'Heading for Outlook announcement Spotlight',
      })}
      actions={isTargetPanelNav ? [openAction, dismissAction] : [dismissAction]}
      dialogPlacement={isTargetPanelNav ? 'top center' : 'right top'}
      dialogWidth={270}
      targetRadius={3}
      target={target}
      targetOnClick={onClickSpotlightTarget}
      targetBgColor={token('elevation.surface.overlay', '#FFFFFF')}
      image={outlookAnnouncementSpotlightImage}
      shouldWatchTarget={true}
    >
      <FormattedMessage
        id="templates.outlook_announcement_spotlight.view-your-events"
        defaultMessage="View your events alongside your to-dos, then schedule time to get things done."
        description="Outlook announcement Spotlight"
      />
    </Spotlight>
  );
};
